import { useUserName } from "src/dm/hooks/useUserNames";
import * as s from "./UserName.css";

// ---------------------------------------------------------------------------
// <UserName> — render a friendly display name for a raw `DOMAIN\username`.
//
// Pattern from audit/21-visual-diff.md §12: deal owner, who-created, signature
// actor, history-actor columns showed values like `GiantEagle\1179482` —
// never resolved to a person. Legacy resolved these via the WEBSEC2 deal-
// owner lookup; we now have an equivalent at `/lookups/user-name`.
//
// Behaviour:
//   - While loading: render the raw userId so the layout doesn't jitter.
//   - On success: render the resolved fullName. The API echoes the raw id
//     back as fullName when nothing matches, so unresolved accounts are
//     visually indistinguishable from the previous behaviour.
//   - When `userId` is null/empty: render the placeholder (configurable).
//   - The raw id is always exposed via the `title` attribute so power
//     users can still see exactly what's in the database — useful when a
//     resolved name is ambiguous (multiple people with the same name).
// ---------------------------------------------------------------------------

interface UserNameProps {
  userId: string | null | undefined;
  /** Rendered when `userId` is missing. Defaults to an em-dash. */
  placeholder?: string;
  /** Optional className for size/colour tweaks at the call site. */
  className?: string;
  /**
   * When `false`, render only the resolved fullName without the raw-id
   * tooltip. Default is `true` — most callers benefit from the tooltip.
   */
  showTooltip?: boolean;
}

const UserName = ({
  userId,
  placeholder = "—",
  className,
  showTooltip = true,
}: UserNameProps) => {
  const { data, isLoading } = useUserName(userId);

  if (!userId || userId.trim().length === 0) {
    return <span className={className ?? s.muted}>{placeholder}</span>;
  }

  // While the resolver is in flight, render the raw id so the column
  // width doesn't reflow when the response lands.
  const display = isLoading ? userId : data?.fullName ?? userId;

  return (
    <span
      className={className ?? s.name}
      title={showTooltip ? userId : undefined}
    >
      {display}
    </span>
  );
};

export default UserName;
